import {useMemo} from "react";
import {SiteConfigurationKey} from "@devour/client";
import {useGetSiteConfiguration} from "@/hooks/useGetSiteConfiguration";

/**
 * Master Drop-In visibility / killswitch — backed by
 * `SiteConfigurationKey.DROP_IN_VISIBLE.isActive`.
 *
 * Default-on: when the row doesn't exist or the fetch hasn't resolved
 * yet, returns `true`. Only an explicit `isActive: false` from admin
 * hides the feature, mirroring the backend `isDropInVisible()` helper.
 *
 * Use this hook in every Drop-In surface (sidebar nav, routes, tiles,
 * modals, deep-link redirects). Single source of truth on the SPA
 * matches the single source of truth on the backend so a 3am admin
 * toggle takes a Drop-In product dark within ~10s end-to-end.
 *
 * NOTE on field name: the generated client enum strips underscores —
 * `DROP_IN_VISIBLE` on the model becomes `DROPINVISIBLE` on the client.
 * Always reference the client form when calling SiteConfigurationKey.
 */
export function useGetDropInVisible(): boolean {
    /*
     * Master killswitch must react fast: align with the backend
     * isDropInVisible() ~10s cache and refetch on tab focus so an admin
     * flip takes the product dark across open sessions within seconds,
     * not the default 60s site-config staleTime.
     */
    const {data: config} = useGetSiteConfiguration(SiteConfigurationKey.DROP_IN_VISIBLE, {
        staleTime: 15000,
        refetchOnWindowFocus: true,
    });
    return useMemo(() => {
        const isActive = (config as { isActive?: boolean } | undefined)?.isActive;
        return isActive !== false;
    }, [config]);
}

/**
 * Resolved-aware variant of {@link useGetDropInVisible}: returns `true` ONLY once
 * the killswitch config has actually returned from the server AND Drop-In is on.
 *
 * Use this to gate FETCHES and to avoid the flash-of-Drop-In: `useGetDropInVisible`
 * is optimistically `true` while the SiteConfiguration request is in flight, which
 * (a) fires speculative `/drop-in` requests that 404 when the feature is off, and
 * (b) flashes nav tabs / sections in, then removes them when the config resolves —
 * more visible on a slow page load. `DropInGate` intentionally keeps the optimistic
 * `useGetDropInVisible` so it never flashes a redirect on a valid Drop-In page; UI
 * that should simply not appear (nav tab, roster row) uses this resolved variant.
 */
export function useGetDropInReady(): boolean {
    const {data: config, isFetched} = useGetSiteConfiguration(SiteConfigurationKey.DROP_IN_VISIBLE, {
        staleTime: 15000,
        refetchOnWindowFocus: true,
    });
    return useMemo(() => {
        const isActive = (config as { isActive?: boolean } | undefined)?.isActive;
        return isFetched && isActive !== false;
    }, [config, isFetched]);
}
